import { RequestOptions } from 'mixpanel-browser';
import { useEffect } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { useStableValue } from '@/hooks/useStableValue';

// beacon is fire-and-forget vs xhr can be blocked if the page unloads
const BEACON: RequestOptions = { transport: 'sendBeacon' };

/**
 * Send `eventName` once for each period that `properties` stay the same, with the time of that period in
 * `$duration`. `mixpanel.time_event` measures the duration; this hook decides when a period starts and ends.
 *
 * A period starts on mount and when the tab becomes visible. It ends when `properties` change, on unmount,
 * and when the tab becomes hidden. The event carries the `properties` of the period that ends.
 *
 * Mixpanel keeps one timer for each event name, so each caller that is mounted at the same time needs its
 * own `eventName`.
 */
export const useTimedUsage = (eventName: string, properties: Record<string, unknown>): void => {
    const mixpanel = useMixpanel();
    const stableProperties = useStableValue(properties);

    useEffect(() => {
        if (!mixpanel) {
            return;
        }

        /*
         * Mixpanel stores the timer in its cookie, and the timer of a closed tab stays there. Track only a
         * period that this effect started, so that a `$duration` never measures from an old start time.
         */
        let isStarted = false;

        const start = () => {
            if (isStarted) {
                return;
            }
            mixpanel.time_event(eventName);
            isStarted = true;
        };

        const end = (options?: RequestOptions) => {
            if (!isStarted) {
                return;
            }
            isStarted = false;
            mixpanel.track(eventName, stableProperties, options);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                end(BEACON);
                return;
            }
            start();
        };

        if (document.visibilityState !== 'hidden') {
            start();
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            end();
        };
    }, [mixpanel, eventName, stableProperties]);
};
